import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu,
  X,
  ChevronDown,
  Shield,
  Users,
  Calendar,
  Building,
  Briefcase,
  Heart,
  ShoppingBag,
  Ticket,
  BarChart,
  TrendingUp,
  Radio,
  Globe,
  BookOpen,
  Mail,
  LogIn
} from 'lucide-react';

const MegaMenu = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  // Logo URL - Michezonet with tagline
  const LOGO_URL = '/michezonet-logo.png';

  const navigation = [
    { name: 'Home', href: '/' },
    {
      name: 'Services',
      href: '/services',
      dropdown: [
        { name: 'Sports Agency', href: '/services#agency', icon: Users },
        { name: 'Sports Technology', href: '/services#technology', icon: BarChart },
        { name: 'Governance & Advisory', href: '/services#governance', icon: Shield },
        { name: 'Commercial & Sponsorship', href: '/services#commercial', icon: TrendingUp },
        { name: 'Events & Operations', href: '/services#events', icon: Calendar },
        { name: 'Media & Communications', href: '/services#media', icon: Radio },
        { name: 'Sports Tourism', href: '/services#tourism', icon: Globe },
        { name: 'Workforce Solutions', href: '/services#workforce', icon: Briefcase }
      ]
    },
    {
      name: 'Technology',
      href: '/platform',
      dropdown: [
        { name: 'MyGolfHub', href: '/platform#mygolfhub', icon: Globe },
        { name: 'Platform Overview', href: '/platform', icon: Building },
        { name: 'Sports Directory', href: '/directory', icon: Users }
      ]
    },
    { name: 'Programs', href: '/programs' },
    { name: 'Events', href: '/events' },
    { name: 'Insights', href: '/insights' },
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' }
  ];

  const isActive = (href) => location.pathname === href;

  return (
    <header className="fixed top-0 left-0 right-0 z-[9998] bg-black/95 backdrop-blur-md border-b border-white/5">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_URL} 
              alt="Michezonet - Your Passion is Our Business" 
              className="h-10 lg:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navigation.map((item) => (
              <div
                key={item.name}
                className="relative"
                onMouseEnter={() => item.dropdown && setActiveDropdown(item.name)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={item.href}
                  className={`px-3 py-2 text-sm font-medium flex items-center gap-1 transition-colors ${
                    isActive(item.href)
                      ? 'text-[#008000]'
                      : 'text-white/80 hover:text-white'
                  }`}
                >
                  {item.name}
                  {item.dropdown && <ChevronDown size={14} />}
                </Link>

                {/* Dropdown */}
                <AnimatePresence>
                  {item.dropdown && activeDropdown === item.name && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute top-full left-0 mt-2 w-72 bg-card-bg border border-border rounded-xl shadow-xl z-[9999]"
                    >
                      <div className="p-2 max-h-[70vh] overflow-y-auto">
                        {item.dropdown.map((subItem) => (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            className="flex items-center gap-3 px-4 py-3 rounded-lg text-white/80 hover:text-white hover:bg-white/5 transition-colors"
                          >
                            <subItem.icon size={18} className="text-primary" />
                            <span className="text-sm">{subItem.name}</span>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/login"
              className="px-4 py-2 text-sm font-medium text-white/80 hover:text-white flex items-center gap-2"
            >
              <LogIn size={16} />
              Login
            </Link>
            <Link
              to="/dashboard"
              className="px-4 py-2 text-sm font-medium bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
            >
              Dashboard
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-card-bg border-t border-border fixed top-16 left-0 right-0 z-[9999] max-h-[80vh] overflow-y-auto"
          >
            <div className="px-4 py-6 space-y-2">
              {navigation.map((item) => (
                <div key={item.name}>
                  <Link
                    to={item.href}
                    className={`block px-4 py-3 rounded-lg text-sm font-medium ${
                      isActive(item.href)
                        ? 'bg-primary/10 text-primary'
                        : 'text-white/80 hover:text-white hover:bg-white/5'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                  {item.dropdown && (
                    <div className="ml-4 mt-1 space-y-1">
                      {item.dropdown.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.href}
                          className="block px-4 py-2 text-sm text-white/60 hover:text-white"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="pt-4 border-t border-border flex gap-3">
                <Link
                  to="/login"
                  className="flex-1 px-4 py-3 text-center text-sm font-medium border border-border text-white rounded-lg"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Login
                </Link>
                <Link
                  to="/dashboard"
                  className="flex-1 px-4 py-3 text-center text-sm font-medium bg-primary text-white rounded-lg"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default MegaMenu;
